/* eslint-disable no-debugger */
import { useEffect, useState } from "react";
import { useNavigate } from "@usas/ui-web/wrappers/ReactRouterDom";
import NavLinks from "../NavLinks";
import useClubMeetsData from "../../../state/clubMeets/UseClubMeetsData";
import useEmbeddedReportData from '@usas/ui-web/state/embeddedReport/UseEmbeddedReportData';
import useReportPeriodSeasonData from "@usas/ui-web/state/reportPeriodSeason/UseReportPeriodSeasonData";
import useBaseUsasClubData from "@usas/ui-web/state/clubBase/UseBaseUsasClubData";
import useMeetSessionsData from "@usas/ui-web/state/meetSessions/UseMeetSessionsData";
import useEnvironmentVariableData from "@usas/ui-web/state/environmentVariable/UseEnvironmentVariableData";
import { navigateToCrossUiRoute } from '@usas/ui-web/utils/FormatCrossUiRoute';
import useGlobalRoutesData from '@usas/ui-web/state/security/UseGlobalRoutesData';
import Constants from "@usas/ui-web/utils/Constants";
import UseForm from "@usas/ui-web/utils/UseForm";
import { formatDate } from "@usas/ui-web/utils/DateFunctions";

const INITIAL_VIEW_STATE = {
  reportParameters: { MeetId: '' },
  MeetId: '',
  routeName: '',
  isLoading: false,
  reportViewerType: '',
  reportName: ''
};

const UseClubMeetsApproved = () => {
  const navigate = useNavigate();
  const { baseUsasClubState } = useBaseUsasClubData();
  const { clubApprovedMeetsState, getClubApprovedMeets, deleteClubMeet, confirmSave } = useClubMeetsData();
  const { reportPeriodSeasonState } = useReportPeriodSeasonData();
  const { resetMeetSessionsData } = useMeetSessionsData();
  const { environmentVariableState, USAS_ORG_UNIT_ID } = useEnvironmentVariableData();
  const { formState, errorState, onValueTextPairChanged, setFormState} = UseForm(getInitialFormState, () => { }, () => { });
  const [deleteModalState, setDeleteModalState] = useState(getInitialDeleteModalState);
  const [gridState, setGridState] = useState([]);
  const { getGlobalRoute } = useGlobalRoutesData();
  const { getEmbeddedReportListForRoute } = useEmbeddedReportData();
  const [viewState, setViewState] = useState(INITIAL_VIEW_STATE);

  const onModalDeleteClicked = () => {
    deleteClubMeet(deleteModalState.meetId);
    setDeleteModalState(getInitialDeleteModalState);
  };

  const onModalCancelClicked = () => {
    setDeleteModalState(getInitialDeleteModalState);
  };

  const onRequestApprovedMeetButtonClicked = () => {
    resetMeetSessionsData();
    navigate(NavLinks.CLUB_MEETS_APPROVED_REQUEST);
  };

  const onDeleteClicked = (meet) => {
    setDeleteModalState({
      ...deleteModalState,
      displayPopUp: true,
      meetId: meet.meetId,
      meetName: meet.meetName,
      meetDates: `${formatDate(meet.startDate)} - ${formatDate(meet.endDate)}`
    });
  };

  const onUploadClicked = (meet) => {
    navigate(NavLinks.CLUB_MEETS_APPROVED_UPLOAD, { state: { meet } });
  };

  const onMeetReconClicked = (meet) => {
    navigate(NavLinks.CLUB_MEETS_APPROVED_RECON, { state: { meet } });
  }

  const onReportPeriodChanged = (value, valueLabel) => {
    onValueTextPairChanged(value, 'reportPeriodId', valueLabel, 'reportPeriodLabel');
  };

  const onEdit = (e, meet) => {
    if (e && e.preventDefault) {
      e.preventDefault();

      navigate(NavLinks.CLUB_MEETS_APPROVED_REQUEST, { state: { meet } });
    }
  }

  const onGoToCart = (meet) => {
    const paymentsRoute = getGlobalRoute('PAYMENTS_SHOPPING_CART');

    if (paymentsRoute && paymentsRoute.route) {
      navigateToCrossUiRoute(paymentsRoute.uIProjectName, paymentsRoute.route, navigate);
    }
  }

  const onViewMeetAnnouncementReport = (meet) => {
    setViewState({
      ...viewState,
      reportParameters: { MeetId: meet.meetId },
      routeName: 'CLUB_MEETS_SANCTION_V3',
      isLoading: true,
      reportViewerType: 'ActiveReports',
      reportName: 'Meet Announcement'
    });
  }

  const onViewUploadResultsInvoice = (meet) => {
    setViewState({
      ...viewState, 
      reportParameters: { MeetId: meet.meetId },
      routeName: 'CLUB_MEETS_SANCTION_V3',
      isLoading: true,
      reportViewerType: 'ActiveReports',
      reportName: 'Meet Sanction Invoice Report'
    });
  } 

  useEffect(() => {
    if (viewState.routeName) {
      const reportListPromise = getEmbeddedReportListForRoute(viewState.routeName);

      if (reportListPromise) {
        reportListPromise.then((stateObj) => {
          if (Array.isArray(stateObj.arrayData)) {
            const reportIdx = stateObj.arrayData.findIndex(x => x.reportName === viewState.reportName);

            setViewState({
              ...viewState,
              isLoading: false,
              showReport: stateObj.arrayData.length > 0,
              reportInfoId: stateObj.arrayData[reportIdx].reportInfoId,
            });
          } else {
            setViewState({
              ...viewState,
              isLoading: false,
              showReport: false,
              reportInfoId: -1
            });
          }
        }).catch((err) => {
          setViewState({
            ...viewState,
            isLoading: false,
            showReport: false,
            reportInfoId: -1
          });
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewState.routeName, viewState.reportViewerType]);

  useEffect(() => {
    if (reportPeriodSeasonState.isArrayLoading === false && reportPeriodSeasonState.isArrayLoaded === true) {
      const today = new Date();
      let todayFormatted;
      todayFormatted = new Date(formatDate(today));

      const currentReportPeriods = [];
      const futureReportPeriods = [];
      let startDate;
      let endDate;
      for (let i = 0; i < reportPeriodSeasonState.arrayData.length; i++) {
        const selectedArrayData = reportPeriodSeasonState.arrayData[i];
        startDate = new Date(formatDate(selectedArrayData.startDate));
        endDate = new Date(formatDate(selectedArrayData.endDate));
        if (todayFormatted <= endDate && todayFormatted >= startDate) {
          currentReportPeriods.push(selectedArrayData);
        }
        if (todayFormatted < startDate) {
          futureReportPeriods.push(selectedArrayData);
        }
      }
      const arrayDataCopy = JSON.parse(JSON.stringify(reportPeriodSeasonState.arrayData));
      if (currentReportPeriods.length > 0) {
        const currentOption = arrayDataCopy.find(x => x.reportPeriodId === currentReportPeriods[0].reportPeriodId);
        setFormState({ ...formState, reportPeriodId: currentOption.reportPeriodId, reportPeriodLabel: currentOption.periodName });
      }
      else if (futureReportPeriods.length > 0) {
        const futureOption = arrayDataCopy.find(x => x.reportPeriodId === futureReportPeriods[0].reportPeriodId);
        setFormState({ ...formState, reportPeriodId: futureOption.reportPeriodId, reportPeriodLabel: futureOption.periodName });
      }
      else {
        const currentOption = arrayDataCopy[arrayDataCopy.length - 1];
        setFormState({ ...formState, reportPeriodId: currentOption.reportPeriodId, reportPeriodLabel: currentOption.periodName });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps   
  }, [reportPeriodSeasonState]);

  useEffect(() => {
    if (clubApprovedMeetsState.isSaved === false && baseUsasClubState.objData.orgUnitId && formState.reportPeriodId > 0) {
      getClubApprovedMeets(baseUsasClubState.objData.orgUnitId, formState.reportPeriodId, USAS_ORG_UNIT_ID);
    } else if (clubApprovedMeetsState.isSaved === true) {
      confirmSave();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clubApprovedMeetsState.isSaved, formState.reportPeriodId]);

  useEffect(() => {
    if (clubApprovedMeetsState.isArrayLoading === false && clubApprovedMeetsState.isArrayLoaded === true) {

      const today = new Date();
      const newMeetArray = [];

      for (const meet of clubApprovedMeetsState.arrayData.filter(
        x => x.sanctionOrgUnitId === USAS_ORG_UNIT_ID && x.meetApprovalRequestId > 0
      )) {
        const meetStartDate = new Date(meet.startDate);

        newMeetArray.push({
          ...meet,
          isPastMeet: today >= meetStartDate
        });
      }

      setGridState(newMeetArray);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clubApprovedMeetsState]); 

  function getInitialFormState() {
    return {
      reportPeriodId: Constants.DEFAULT_ID,
      reportPeriodLabel: ''
    };
  };

  function getInitialDeleteModalState() {
    return {
      displayPopUp: false,
      meetId: Constants.DEFAULT_ID,
      meetName: '',
      meetDates: ''
    };
  };

  return {
    clubApprovedMeetsState,
    gridState,
    formState,
    errorState,
    deleteModalState,
    viewState,
    setViewState,
    isSaving: clubApprovedMeetsState.isSaving,
    isLoading: environmentVariableState.isLoading || baseUsasClubState.isObjLoading || clubApprovedMeetsState.isArrayLoading,
    organizationId: baseUsasClubState.objData?.orgUnit?.organizationId || Constants.DEFAULT_ID,
    clubOrgUnitId: baseUsasClubState.objData?.orgUnitId || '',
    onRequestApprovedMeetButtonClicked,
    onDeleteClicked,
    onReportPeriodChanged,
    onUploadClicked,
    onMeetReconClicked,
    onModalCancelClicked,
    onModalDeleteClicked,
    onViewMeetAnnouncementReport,
    onViewUploadResultsInvoice,
    onEdit,
    onGoToCart,
    USAS_ORG_UNIT_ID,
    environmentVariableState,
    INITIAL_VIEW_STATE
  };
};

export default UseClubMeetsApproved;